import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, FileAudio, Loader2, Trash2, Upload, Video } from "lucide-react";
import { Button } from "../components/ui/button";
import { apiErrorMessage, formatSize, formatTime } from "./pitchTempoUtils";
import { PUBLIC_TOOLS_API, deletePublicSession, mediaUrl, publicToolHeaders, usePublicToolsLimits } from "../lib/publicAudioTools";
import { useAudioExportSettings } from "../lib/audioExportFormats";
import AudioExportControls from "../components/audio-tools/AudioExportControls";

const API = `${PUBLIC_TOOLS_API}/video-audio`;

export default function VideoAudioPage() {
  const limits = usePublicToolsLimits();
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  const localUrlRef = useRef("");
  const controllerRef = useRef(null);
  const generationRef = useRef(0);
  const [session, setSession] = useState(null);
  const [original, setOriginal] = useState("");
  const exportSettings = useAudioExportSettings("video");
  const { format, quality } = exportSettings;
  const [results, setResults] = useState({});
  const [phase, setPhase] = useState("EMPTY");
  const [error, setError] = useState("");
  const [audioOnly, setAudioOnly] = useState(false);
  const [previewUnsupported, setPreviewUnsupported] = useState(false);
  const [progress, setProgress] = useState(0);
  const busy = ["UPLOADING", "PROBING", "AUDIO", "VIDEO"].includes(phase);

  useEffect(() => () => {
    generationRef.current += 1;
    controllerRef.current?.abort();
    deletePublicSession("video-audio", sessionRef.current);
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
  }, []);

  const reset = () => {
    generationRef.current += 1;
    controllerRef.current?.abort();
    deletePublicSession("video-audio", sessionRef.current);
    sessionRef.current = null;
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
    localUrlRef.current = "";
    setSession(null);
    setOriginal("");
    setResults({});
    setPhase("EMPTY");
    setError("");
    setAudioOnly(false);
    setPreviewUnsupported(false);
  };

  const upload = async (file) => {
    if (!file || busy) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (exportSettings.catalog?.input_extensions.includes(extension)) {
      setError("Este archivo ya es audio y no contiene una pista de video para separar.");
      setAudioOnly(true);
      return;
    }
    if (!limits.video.formats.includes(extension)) {
      setError("Formato no compatible. Usa MP4, MOV, MKV, WEBM o AVI.");
      return;
    }
    if (!file.size || file.size > limits.video.max_bytes) {
      setError(file.size ? `El video supera ${formatSize(limits.video.max_bytes)}.` : "El archivo está vacío.");
      return;
    }
    reset();
    const generation = ++generationRef.current;
    setPhase("UPLOADING");
    setProgress(0);
    const form = new FormData();
    form.append("file", file);
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const response = await axios.post(`${API}/upload`, form, {
        signal: controller.signal,
        onUploadProgress: (event) => {
          if (event.total) setProgress(Math.round(event.loaded * 100 / event.total));
          if (event.loaded === event.total) setPhase("PROBING");
        },
      });
      if (generation !== generationRef.current) {
        deletePublicSession("video-audio", response.data);
        return;
      }
      sessionRef.current = response.data;
      setSession(response.data);
      localUrlRef.current = URL.createObjectURL(file);
      setOriginal(localUrlRef.current);
      setPhase("READY");
    } catch (failure) {
      if (generation !== generationRef.current) return;
      setError(apiErrorMessage(failure, "No se pudo preparar el video."));
      setAudioOnly(failure.response?.data?.detail?.code === "audio_only");
      setPhase("ERROR");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const extract = async (operation) => {
    if (!session || busy) return;
    const generation = generationRef.current;
    setError("");
    setPhase(operation === "audio" ? "AUDIO" : "VIDEO");
    try {
      const response = await axios.post(`${API}/extract`, {
        session_id: session.session_id, operation, format, ...(operation === "audio" ? { quality } : {}),
      }, { headers: publicToolHeaders(session) });
      if (generation !== generationRef.current) return;
      setResults((current) => ({ ...current, [operation]: { ...response.data, requestedQuality: JSON.stringify(quality) } }));
      setPhase("READY");
    } catch (failure) {
      if (generation !== generationRef.current) return;
      setError(apiErrorMessage(failure, "No se pudo generar el resultado."));
      setPhase("READY");
    }
  };

  const audioResult = results.audio?.format === format && results.audio.requestedQuality === JSON.stringify(quality) ? results.audio : null;
  return (
    <div className="steel-page min-w-0 pb-8" data-testid="video-audio-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-border pb-5">
        <div className="min-w-0">
          <p className="mb-1 text-xs font-semibold uppercase text-signal">Herramientas gratuitas</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Extraer audio de video</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sube un video y descarga su audio por separado o el video sin audio.</p>
        </div>
        <span className="text-xs font-semibold text-tune">Gratis · Sin registro</span>
      </div>
      {error && <div role="alert" className="mb-4 rounded border border-destructive/50 bg-destructive/10 p-3 text-sm">
        {error}{audioOnly && <Link to="/pitch-tempo" className="ml-2 underline">Cambiar tono y BPM</Link>}
      </div>}
      <input ref={inputRef} type="file" accept={limits.video.formats.map((item) => `.${item}`).join(",")}
        className="sr-only" aria-label="Seleccionar archivo de video" onChange={(event) => upload(event.target.files?.[0])} />
      {!session ? (
        <div className="flex min-h-80 flex-col items-center justify-center gap-4 rounded-md border-2 border-dashed border-border bg-panel/60 p-6 text-center"
          onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); upload(event.dataTransfer.files?.[0]); }} data-testid="video-dropzone">
          <Video className="h-12 w-12 text-signal" />
          <div><h2 className="text-lg font-semibold">Arrastra tu video aquí</h2>
            <p className="mt-1 text-sm text-muted-foreground">{limits.video.formats.join(", ").toUpperCase()} · hasta {formatSize(limits.video.max_bytes)} y {Math.round(limits.video.max_seconds / 60)} min</p></div>
          <Button onClick={() => inputRef.current?.click()} disabled={busy} className="min-h-11 gap-2 bg-signal text-ink hover:bg-signal/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Seleccionar video
          </Button>
        </div>
      ) : (
        <div className="min-w-0 space-y-5" data-testid="video-audio-editor">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="min-w-0"><p className="truncate font-semibold" title={session.filename}>{session.filename}</p>
              <p className="mt-1 text-xs text-muted-foreground">{session.format.toUpperCase()} · {formatSize(session.file_size)} · {formatTime(session.duration_seconds)}</p></div>
            <Button variant="ghost" size="icon" aria-label="Eliminar video" title="Eliminar video" onClick={reset} disabled={busy} className="h-11 w-11 shrink-0"><Trash2 className="h-4 w-4" /></Button>
          </div>
          {session.browser_preview && !previewUnsupported ? (
            <video controls src={original} aria-label="Video original" onError={() => setPreviewUnsupported(true)}
              className="aspect-video max-h-[460px] w-full bg-black object-contain" preload="metadata" />
          ) : <p className="border-y border-border py-8 text-center text-sm text-muted-foreground">La vista previa no es compatible con este navegador. Puedes generar y descargar los resultados.</p>}
          <dl className="grid grid-cols-2 gap-3 border-y border-border py-4 text-sm sm:grid-cols-4">
            {[['Resolución', `${session.video.width} × ${session.video.height}`], ['Video', session.video.codec],
              ['Audio', session.audio?.codec || 'Sin audio'], ['Canales', session.audio?.channels || '—']].map(([label, value]) => (
              <div key={label}><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 break-words font-semibold">{value}</dd></div>
            ))}
          </dl>
          {!session.audio && <p role="status" className="text-sm text-brass">Este video no contiene una pista de audio.</p>}
          <div className="grid gap-6 md:grid-cols-2">
            <section aria-label="Audio separado" className="min-w-0 space-y-4 border-t border-border pt-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><FileAudio className="h-5 w-5 text-brass" /> Audio</h2>
              <div className="flex flex-wrap items-center gap-3">
                <AudioExportControls id="video-audio-format" settings={exportSettings} disabled={busy} source={{ ...session.audio, duration_seconds: session.duration_seconds }} />
                <Button onClick={() => extract("audio")} disabled={busy || !session.audio || !exportSettings.ready} className="min-h-11 gap-2 bg-brass text-ink hover:bg-brass/90">
                  {phase === "AUDIO" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileAudio className="h-4 w-4" />} Extraer audio
                </Button>
              </div>
              {audioResult && <div className="space-y-3" data-testid="audio-result">
                <audio controls src={mediaUrl(audioResult.url)} aria-label="Audio extraído" className="h-12 w-full" preload="metadata" />
                <a href={`${mediaUrl(audioResult.url)}&download=true`} download={audioResult.filename}
                  className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brass underline underline-offset-4"><Download className="h-4 w-4" /> Descargar {audioResult.format.toUpperCase()}</a>
              </div>}
            </section>
            <section aria-label="Video sin audio" className="min-w-0 space-y-4 border-t border-border pt-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Video className="h-5 w-5 text-signal" /> Video sin audio</h2>
              <Button onClick={() => extract("video")} disabled={busy} variant="outline" className="min-h-11 gap-2 border-signal/40 text-signal">
                {phase === "VIDEO" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Video sin audio
              </Button>
              {results.video && <div className="space-y-3" data-testid="video-result">
                {session.browser_preview && !previewUnsupported && <video controls muted src={mediaUrl(results.video.url)} aria-label="Video sin pista de audio"
                  className="aspect-video w-full bg-black object-contain" preload="metadata" />}
                <a href={`${mediaUrl(results.video.url)}&download=true`} download={results.video.filename}
                  className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-signal underline underline-offset-4"><Download className="h-4 w-4" /> Descargar video</a>
              </div>}
            </section>
          </div>
        </div>
      )}
      <p role="status" aria-live="polite" className="mt-4 min-h-6 text-sm text-signal">
        {phase === "UPLOADING" ? `Subiendo video... ${progress}%` : phase === "PROBING" ? "Preparando archivo..." :
          phase === "AUDIO" ? "Extrayendo audio..." : phase === "VIDEO" ? "Preparando video..." : session ? "Listo." : ""}
      </p>
      <p className="mt-4 text-xs text-muted-foreground">Tus archivos se procesan temporalmente en el servidor y se eliminan automáticamente después de {Math.round(limits.ttl_seconds / 60)} minutos.</p>
    </div>
  );
}
